import type { Context } from 'react';
import { Children, Component, cloneElement } from 'react';

import type { IProps, IQueryContext } from './models';
import { QueryParamsContext } from './context';

export class QueryParamsProvider extends Component<IProps> {
    static contextType: Context<IQueryContext> = QueryParamsContext;

    static defaultProps = {
        preferOwnProps: true,
    };

    componentDidMount() {
        if (!this.context) {
            console.error('QueryProvider has not got context!');
        }
    }

    static safeJsonParse(stringifiedValue: string, defaultValue: string) {
        let value;

        try {
            value = JSON.parse(stringifiedValue);
        } catch (e: any) {
            console.error('QueryProvider could not parse query value');
            console.error(e);
            value = defaultValue;
        }

        return value;
    }

    getQueryParams(queryParams: IQueryContext) {
        const { mappings = [] } = this.props;

        return mappings.reduce(
            (
                sum,
                { defaultValue, propName, queryParamName, tryJsonParse = false, valuesMapping }
            ) => {
                const valueFromParam = queryParams[queryParamName];

                let value;

                if (valuesMapping) {
                    value =
                        valueFromParam && valuesMapping[valueFromParam] !== undefined
                            ? valuesMapping[valueFromParam]
                            : defaultValue;
                } else if (valueFromParam !== undefined) {
                    value = valueFromParam;
                } else if (defaultValue !== undefined) {
                    value = defaultValue;
                }

                return value === undefined
                    ? sum
                    : {
                          ...sum,
                          [propName]: tryJsonParse
                              ? QueryParamsProvider.safeJsonParse(value, defaultValue)
                              : value,
                      };
            },
            {}
        );
    }

    checkExistenceQueryParams(queryParams: IQueryContext) {
        const { existence = [] } = this.props;

        return existence.reduce(
            (sum, { ifDoesNotExistPropValue, ifExistsPropValue, propName, queryParamName }) => {
                const queryExists = queryParams[queryParamName];

                return {
                    ...sum,
                    [propName]: queryExists ? ifExistsPropValue : ifDoesNotExistPropValue,
                };
            },
            {}
        );
    }

    render() {
        const queryParams = this.context as IQueryContext;
        const { children, existence, mappings, preferOwnProps, ...otherProps } = this.props;
        const child = Children.only(children);
        const propsToPass = preferOwnProps
            ? {
                  ...otherProps,
                  ...this.checkExistenceQueryParams(queryParams),
                  ...this.getQueryParams(queryParams),
              }
            : {
                  ...this.checkExistenceQueryParams(queryParams),
                  ...this.getQueryParams(queryParams),
                  ...otherProps,
              };

        return cloneElement(child, propsToPass);
    }
}
